import { useState, useEffect, useCallback } from 'react';
import { languageDetectionService, UserLanguageContext } from '../services/languageDetectionService';

export interface UseLanguageDetectionReturn {
  languageContext: UserLanguageContext | null;
  isLoading: boolean;
  error: string | null;
  refreshLanguageDetection: () => Promise<void>;
  setManualLanguage: (languageCode: string, languageId: string) => void;
  getCurrentLanguageCategoryId: () => string | null;
  getCurrentLanguageCode: () => string | null;
  isInitialized: boolean;
}

export const useLanguageDetection = (): UseLanguageDetectionReturn => {
  const [languageContext, setLanguageContext] = useState<UserLanguageContext | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize language detection on component mount
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('🌍 useLanguageDetection: Initializing language detection...');
        const context = await languageDetectionService.initializeLanguageDetection();
        
        setLanguageContext(context);
        setIsInitialized(true);
        
        console.log('🌍 useLanguageDetection: Language detection completed:', context);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Language detection failed';
        setError(errorMessage);
        console.error('❌ useLanguageDetection: Language detection failed:', err);
        
        // Set fallback language context
        setLanguageContext({
          countryCode: 'US',
          browserLanguages: ['en'],
          selectedLanguage: { language_code: 'en', language_id: '11703' },
          fallbackToFirstLanguage: true
        });
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Only initialize if not already initialized
    if (!isInitialized && !languageDetectionService.isLanguageDetectionInitialized()) {
      initializeLanguage();
    } else if (languageDetectionService.isLanguageDetectionInitialized()) {
      // If service is already initialized, get the context
      const existingContext = languageDetectionService.getCurrentLanguageContext();
      setLanguageContext(existingContext);
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Refresh language detection
  const refreshLanguageDetection = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🌍 useLanguageDetection: Refreshing language detection...');
      const context = await languageDetectionService.refreshLanguageDetection();
      
      setLanguageContext(context);
      console.log('🌍 useLanguageDetection: Language detection refreshed:', context);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Language detection refresh failed';
      setError(errorMessage);
      console.error('❌ useLanguageDetection: Language detection refresh failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Set manual language
  const setManualLanguage = useCallback((languageCode: string, languageId: string) => {
    try {
      console.log(`🌍 useLanguageDetection: Setting manual language: ${languageCode} (${languageId})`);
      
      languageDetectionService.setLanguage(languageCode, languageId);
      const updatedContext = languageDetectionService.getCurrentLanguageContext();
      
      setLanguageContext(updatedContext);
      setError(null);
      
      console.log('🌍 useLanguageDetection: Manual language set:', updatedContext);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to set manual language';
      setError(errorMessage);
      console.error('❌ useLanguageDetection: Failed to set manual language:', err);
    }
  }, []);

  // Get current language category ID
  const getCurrentLanguageCategoryId = useCallback((): string | null => {
    return languageDetectionService.getCurrentLanguageCategoryId();
  }, []);

  // Get current language code
  const getCurrentLanguageCode = useCallback((): string | null => {
    return languageDetectionService.getCurrentLanguageCode();
  }, []);

  return {
    languageContext,
    isLoading,
    error,
    refreshLanguageDetection,
    setManualLanguage,
    getCurrentLanguageCategoryId,
    getCurrentLanguageCode,
    isInitialized
  };
}; 